import debug from "debug";
import EventHub from "../Experience/Utils/EventManagement/EventHub";
import LoginManager from "../Components/Keycloak/LogInManager.ts";
import { baseApiUrl } from "../Experience/World/config.js";
const eventHub = EventHub.getInstance();
// Expose eventHub globally for inline scripts
window.eventHub = eventHub;
let user_authenticated = false;
window.user_authenticated = user_authenticated;
let user_data = false;
const log = debug("app:fn");
// Expose log globally for inline scripts
window.log = log;

/**
 * Creates a new design project.
 * Now validates design name before creation to check for duplicates.
 * @param {HTMLElement} el - The element that triggered the function.
 * @param {string} id - The modal ID to close after creation.
 * @category ToolbarScripts
 */
function createNewDesign(el, id) {
  const parentContainer = el.closest("div");
  const designName = parentContainer
    .querySelector('input[type="text"]')
    .value.trim();

  if (el.parentNode.querySelector("#design-name").value != "") {
    // Hide any existing alerts
    for (
      let i = 0;
      i < document.querySelectorAll("#design-name-alert").length;
      i++
    ) {
      document.querySelectorAll("#design-name-alert")[i].style.display = "none";
    }
    document.getElementById("design-name-alert").style.display = "none";
    
    // Emit requestNewProject event which DesignManager will handle with validation
    // This ensures duplicate checking happens before creating the project
    let data = {
      event: "requestNewProject",
      name: designName,
    };
    log("EVENTHUB EVENT: requestNewProject with validation", data);
    eventHub.emit("requestNewProject", data);
    
    // Note: Modal closing and UI updates will happen in DesignManager after validation
    // If validation passes, DesignManager will emit "newProject" which triggers the scene creation
    // If validation fails (conflict), DesignManager will show conflict dialog
  } else {
    // Show alert for empty name
    for (
      let i = 0;
      i < document.querySelectorAll("#design-name-alert").length;
      i++
    ) {
      document.querySelectorAll("#design-name-alert")[i].style.display =
        "block";
    }
  }
}
window.createNewDesign = createNewDesign;

/**
 * Renames an existing design project.
 * @param {HTMLElement} el - The element that triggered the function.
 * @param {string} id - The modal ID to close after renaming.
 */
function renameDesign(el, id) {
  if (el.parentNode.querySelector("#rename-name").value != "") {
    for (
      let i = 0;
      i < document.querySelectorAll("#rename-name-alert").length;
      i++
    ) {
      document.querySelectorAll("#rename-name-alert")[i].style.display = "none";
    }
    document.getElementById("rename-name-alert").style.display = "none";
    const newName = el.parentNode.querySelector("#rename-name").value;
    document.getElementById("toolbar-project-name").value = newName;
    closeModal(id);
    const data = { event: "designRenamed", designName: newName };

    eventHub.emit("designRenamed", data);
    log({ event: "designRenamed", designName: newName });
  } else {
    for (
      let i = 0;
      i < document.querySelectorAll("#rename-name-alert").length;
      i++
    ) {
      document.querySelectorAll("#rename-name-alert")[i].style.display =
        "block";
    }
  }
}
window.renameDesign = renameDesign;

/**
 * Fetches saved design projects from the server using Keycloak authentication.
 * Now uses DataVault for centralized data management and caching.
 * @param {Object} userData - Legacy parameter (not used, kept for backward compatibility).
 * @param {Function} cb - The callback function to process the response data.
 */
function getSavedDesigns(userData, cb) {
  log("Getting saved designs from DataVault");
  
  // Clear the list and show loading indicator
  document.querySelectorAll(".saved-design-list").forEach((designListElement) => {
    designListElement.innerHTML = "";
    const loadingLi = document.createElement("li");
    loadingLi.style.cssText = "padding: 10px; text-align: center; color: #666; font-style: italic;";
    loadingLi.textContent = "Loading designs...";
    designListElement.append(loadingLi);
  });
  
  // Check if DataVault is available
  const dataVault = window.experience?.dataVault;
  
  if (dataVault) {
    // Always fetch fresh data when button is clicked
    log("Fetching fresh designs from backend...");
    
    // Set up a timeout in case the fetch never completes
    const timeout = setTimeout(() => {
      log("Designs fetch timeout - returning empty array");
      eventHub.off("designsData", designsHandler);
      cb([]);
    }, 10000);
    
    // Listen for designsData event
    const designsHandler = (designs) => {
      clearTimeout(timeout);
      log("Received fresh designsData event from DataVault");
      cb(designs || []);
    };
    
    eventHub.once("designsData", designsHandler);
    
    // Always trigger fresh fetch
    log("Triggering fresh fetchDesigns in DataVault");
    dataVault.fetchDesigns().catch((error) => {
      log("Error fetching designs from DataVault:", error);
      clearTimeout(timeout);
      eventHub.off("designsData", designsHandler);
      cb([]);
    });
  } else {
    // Fallback to old method if DataVault not available
    log("DataVault not available, using fallback fetch method");
    const url = `${baseApiUrl}/design`;
    
    // Use eventHub pattern for decoupled authentication (same pattern DataProvider uses)
    eventHub.emit("tokenRequest");
    
    // Add timeout to prevent hanging if tokenResponse never comes
    const timeout = setTimeout(() => {
      log("Token request timeout - LoginManager may not be initialized");
      cb([]);
    }, 5000);

    log("Token request emitted");
    
    eventHub.once("tokenResponse", async (token) => {
      clearTimeout(timeout);
      try {
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        const response = await fetch(url, { headers });

        if (!response.ok) {
          log(`Failed to fetch saved designs: ${response.status} ${response.statusText}`);
          cb([]);
          return;
        }

        const data = await response.json();
        
        // Handle different response structures
        // API might return { data: [...] } or just [...]
        const designs = Array.isArray(data) ? data : (data.data || data.items || []);
        
        cb(designs);
        log("Designs fetched", designs);
      } catch (error) {
        log("Error fetching saved designs:", error);
        cb([]);
      }
    });
  }
}

/**
 * Displays saved design projects in the UI.
 * @param {Array<Object>} data - List of saved design projects.
 */
function showSavedDesigns(data) {
  // Clear loading indicator and display results
  document
    .querySelectorAll(".saved-design-list")
    .forEach((designListElement) => {
      designListElement.innerHTML = "";
      
      if (data && data.length > 0) {
        log(`Displaying ${data.length} designs`);
        data.forEach((design) => {
          const li = document.createElement("li");
          const labelSpan = document.createElement("span");
          const openSpan = document.createElement("span");

          labelSpan.textContent = design.name;
          openSpan.textContent = "Open";

          li.addEventListener("click", () => {
            // Handle both uuid and id properties from the design object
            const designId = design.uuid || design.id || design.designId;
            log("Design object:", design);
            log("Design ID extracted:", designId);
            
            if (!designId) {
              log("ERROR: No UUID or ID found in design object:", design);
              return;
            }
            
            const messageData = {
              event: "viewProject",
              uuid: designId,
            };
            log("EVENTHUB EVENT: ", messageData);
            eventHub.emit("viewProject", messageData);
            log("SENT EVENT TO EVENTHUB", messageData);
          });

          li.append(labelSpan, openSpan);
          designListElement.append(li);
        });
      } else {
        // Show empty state
        const emptyLi = document.createElement("li");
        emptyLi.style.cssText = "padding: 10px; text-align: center; color: #666; font-style: italic;";
        emptyLi.textContent = "No saved designs found";
        designListElement.append(emptyLi);
      }
    });
}

/**
 * Initializes the saved designs list on UI load.
 * @param {Array<Object>} data - List of saved design projects.
 */
function showInitSavedDesigns(data) {
  if (data.length > 0) {
    document.getElementById("no-saved-design").style.display = "none";
    document.getElementById("card-saved-design-list").style.display = "block";
    document
      .querySelectorAll(".saved-design-list")
      .forEach((designListElement) => {
        designListElement.innerHTML = "";
        log(designListElement.innerHTML);
        data.forEach((design) => {
          const li = document.createElement("li");
          const labelSpan = document.createElement("span");
          const openSpan = document.createElement("span");

          labelSpan.textContent = design.name;
          openSpan.textContent = "Open";

          li.addEventListener("click", () => {
            // Handle both uuid and id properties from the design object
            const designId = design.uuid || design.id || design.designId;
            log("Design object:", design);
            log("Design ID extracted:", designId);
            
            if (!designId) {
              log("ERROR: No UUID or ID found in design object:", design);
              return;
            }
            
            const messageData = {
              event: "viewProject",
              uuid: designId,
            };
            log("EVENTHUB EVENT: ", messageData);
            eventHub.emit("viewProject", messageData);
            document.getElementById("backgroundModal").style.display = "none";
            document.getElementById(
              "toolbar-project-name-container"
            ).style.display = "block";
          });

          li.append(labelSpan, openSpan);
          designListElement.append(li);
        });
      });
  } else {
    document.getElementById("card-saved-design-list").style.display = "none";
    document.getElementById("no-saved-design").style.display = "flex";
  }
}

/**
 * Toggles the display of a dropdown element.
 * @param {Event} event - The event object.
 * @param {string} id - The ID of the dropdown element.
 */
function toggleDropdown(event, id) {
  if (document.getElementById(id).style.display != "flex") {
    document.getElementById(id).style.display = "flex";
  } else {
    document.getElementById(id).style.display = "none";
  }
  event.stopPropagation();
}
window.toggleDropdown = toggleDropdown;

// function addToCart() {
//   let data = {
//     event: "addToCart",
//   };
//   if (window.parent) {
//     console.log("MESSAGE TO PARENT: ", data);
//     window.parent.postMessage(data, "*");
//   }
// }
// window.addToCart = addToCart;

function viewCart() {
  let data = {
    event: "viewCart",
  };
  log("EVENTHUB EVENT: ", data);
  eventHub.emit("viewCart", data);
}
window.viewCart = viewCart;

/**
 * Displays a notification snackbar with the given text.
 * @param {string} text - The message to display.
 */
function showSnackbar(text) {
  var snackbar = document.getElementById("snackbar");
  snackbar.innerHTML = '<i class="fa-solid fa-check"></i> ' + text;
  snackbar.className = "show";

  setTimeout(function () {
    snackbar.className = snackbar.className.replace("show", "");
  }, 3000);
}

/**
 * Toggles the visibility of an element.
 * @param {string} id - The ID of the element to toggle.
 */
function toggleElement(id) {
  let el = document.getElementById(id);
  const wasHidden = el.style.display != "block";
  
  if (wasHidden) {
    el.style.display = "block";
  } else {
    el.style.display = "none";
  }

  // Refresh saved designs list whenever the dropdown is opened
  // NOTE: Replaced with ViewDesignsModal - old dropdown no longer used
  // if (id == "options-saved-design" && wasHidden) {
  //   log("Refreshing saved designs list...");
  //   getSavedDesigns(user_data, showSavedDesigns);
  // }
}
window.toggleElement = toggleElement;

/**
 * Sets up event listeners for dropdown toggling on page load.
 */
document.addEventListener("DOMContentLoaded", function () {
  document.getElementById("saveOptions").addEventListener("click", (event) => {
    toggleDropdown(event, "save-menu-dropdown");
  });
});

/**
 * Handles the display of the power table based on shopping cart items.
 * @param {string|null} action - Action to perform (e.g., "update").
 */
function setPowerTable(action = null) {
  let powerModal = document.getElementById("power-modal");
  if (
    (powerModal.style.display != "block" && !action) ||
    (action == "update" && powerModal.style.display == "block")
  ) {
    powerModal.style.display = "block";
    let powerTable = document.getElementById("powerTable");
    if (smartCart.items.length != 0) {
      document.getElementById("power-alert").style.display = "none";
      powerTable.parentNode.style.display = "block";
      powerTable.innerHTML = "";
      let estimated = 0;

      for (let i = 0; i < smartCart.items.length; i++) {
        if (smartCart.items[i]["data"]["softDelete"] == false) {
          const element = smartCart.items[i];
          let tr = document.createElement("tr");
          let idTd = document.createElement("td");
          idTd.innerHTML = i + 1;
          let partNumberTd = document.createElement("td");
          partNumberTd.innerHTML = element["sku"];
          let quantityTd = document.createElement("td");
          quantityTd.innerHTML = element["qty"];
          let priceTd = document.createElement("td");
          priceTd.innerHTML = element["data"]["Price"].toFixed(2);
          let totalTd = document.createElement("td");
          totalTd.innerHTML =
            "$" + (element["qty"] * element["data"]["Price"]).toFixed(2);
          estimated += parseFloat(
            (element["qty"] * element["data"]["Price"]).toFixed(2)
          );
          tr.append(idTd, partNumberTd, quantityTd, priceTd, totalTd);
          powerTable.appendChild(tr);
        }
      }
      document.getElementById("estimated").innerHTML =
        "$ " + estimated.toFixed(2);
    } else {
      document.getElementById("power-alert").style.display = "block";
      powerTable.parentNode.style.display = "none";
    }
  } else {
    closeModal("power-modal");
  }
}

/**
 * Closes a modal window.
 * @param {string} id - The ID of the modal to close.
 */ 
function closeModal(id) {
  // document.getElementById(id).style.display = "none";
  document.getElementById("loadModal").style.display = "none";
  document.getElementById("save-modal").style.display = "none";
  document.getElementById("new-modal").style.display = "none";
  document.getElementById("rename-modal").style.display = "none";
  document.getElementById("backgroundModal").style.display = "none";
}
window.closeModal = closeModal;

let element = document.querySelector("[class = configurator-container]");
let button = document.querySelector("[class = design-list]");

/**
 * Toggles the active state of a configuration menu.
 */
function toggleConfig() {
  if (!element.classList.contains("menu-active")) {
    button.style.display = "none";
    element.classList.add("menu-active");
  } else {
    button.style.display = "flex";
    element.classList.remove("menu-active");
  }
  sizeMenu();
}

/**
 * Toggles the visibility of specific parts based on user selection.
 * @param {HTMLElement} el - The element that triggered the event.
 * @param {string} type - The type of part to toggle.
 */
function togglePart(el, type) {
  if (el.id == "cover-checkbox") {
    if (el.checked) {
      showPart(["COVER"], "threed");
    } else {
      hidePart(["COVER"], "threed");
    }
  } else {
    showHidePart(type, el.checked);
  }
}

document.querySelectorAll(".option").forEach((el) => {
  el.addEventListener("click", () => {
    selectedOption(el);
  });
});

function selectedOption(el) {
  let dots = document.querySelectorAll(".option");
  if (el.classList.contains("selected")) {
    el.classList.remove("selected");
  } else {
    for (let i = 0; i < dots.length; i++) {
      dots[i].classList.remove("selected");
      el.classList.add("selected");
    }
    el.classList.add("selected");
  }
}

document.getElementById("top_view").addEventListener("click", () => {
  if (!document.getElementById("top_view").classList.contains("selected")) {
    document.getElementById("top_view").classList.add("selected");
    document.getElementById("bottom_view").classList.remove("selected");
  }
});

document.getElementById("bottom_view").addEventListener("click", () => {
  if (!document.getElementById("bottom_view").classList.contains("selected")) {
    document.getElementById("bottom_view").classList.add("selected");
    document.getElementById("top_view").classList.remove("selected");
  }
});

/**
 * Opens the browser in fullscreen mode.
 */
function openFullscreen() {
  let elem = document.querySelector(".webgl");
  if (elem.requestFullscreen) {
    elem.requestFullscreen();
  } else if (elem.webkitRequestFullscreen) {
    elem.webkitRequestFullscreen();
  } else if (elem.msRequestFullscreen) {
    elem.msRequestFullscreen();
  }
}

/**
 * Changes the mode of the application based on user selection.
 * @param {HTMLElement} el - The element containing the selected mode.
 */
function changeMode(el) {
  let data = {
    action: el.value,
  };
  log(data);
  iframe.contentWindow.postMessage(data, "*");
}

/**
 * Switches the probe connector type and updates pricing in the shopping cart.
 * @param {string} value - The selected mode ("production" or "development").
 */
function switchProbeConnector(value) {
  if (value == "production") {
    for (let i = 0; i < smartCart.items.length; i++) {
      const element = smartCart.items[i];
      if (element["data"]["type"] == "probe") {
        smartCart.items[i]["sku"] = "XM-292-F-2H-0.5-01";
        smartCart.items[i]["name"] = "XM-292-F-2H-0.5-01";
        smartCart.items[i]["data"]["Price"] = 35.0;
      }
    }
  } else {
    for (let i = 0; i < smartCart.items.length; i++) {
      const element = smartCart.items[i];
      if (element["data"]["type"] == "probe") {
        smartCart.items[i]["sku"] = "XM-PB1-292F";
        smartCart.items[i]["name"] = "XM-PB1-292F";
        smartCart.items[i]["data"]["Price"] = 275.95;
      }
    }
  }
  setBomTable("update");
}

/**
 * Requests a quote for the items in the cart.
 */
function requestForQuote() {
  let array = [];
  for (let i = 0; i < smartCart.items.length; i++) {
    if (smartCart.items[i]["data"]["softDelete"] == false) {
      array.push({
        part_number: smartCart.items[i]["sku"],
        qty: smartCart.items[i]["qty"],
        price: smartCart.items[i]["data"]["Price"],
      });
    }
  }
  let data = {
    data: array,
  };
  if (parentEvt) {
    log("calling parent event");
    parentEvt.source.postMessage(data, parentEvt.origin);
  }
}

document.querySelector("#sign-in").addEventListener("click", () => {
  let data = {
    event: "sign_in",
  };
  log("EVENTHUB EVENT: ", data);
  eventHub.emit("sign_in", data);
});

// Handle sign-out button click
const signOutElement = document.querySelector(".signOut");
if (signOutElement) {
  signOutElement.addEventListener("click", () => {
    // Hide dashboard trigger
    document.getElementById("dashboardTrigger").style.display = "none";
    // Call LoginManager logout (this will trigger Keycloak logout)
    try {
      const loginManager = LoginManager.getInstance();
      loginManager.logout();
      // Also emit guest event to update UI immediately
      eventHub.emit("auth:guest");
      // Also emit sign_out event
      let data = {
        event: "sign_out",
      };
      log("EVENTHUB EVENT: ", data);
      eventHub.emit("sign_out", data);
    } catch (error) {
      log("Error during logout:", error);
      // Fallback: just update UI to guest state
      guest();
    }
  });
}

document.querySelector(".myInfo").addEventListener("click", () => {
  let data = {
    event: "myInfo",
  };
  log("EVENTHUB EVENT: ", data);
  eventHub.emit("myInfo", data);
});

function closeConfigurationModal() {
  let sidebarModal = document.querySelector(".configurationModal");
  let toggleSidebar = document.getElementById("toggleSidebar");
  sidebarModal.classList.remove("active");
  toggleSidebar.classList.remove("pressed");
  sidebarModal.addEventListener("transitionend", function onTransitionEnd() {
    if (!sidebarModal.classList.contains("active")) {
      sidebarModal.style.display = "none";
    }
    sidebarModal.removeEventListener("transitionend", onTransitionEnd);
  });
}
window.closeConfigurationModal = closeConfigurationModal;

function toggleMenu() {
  let container = document.querySelector(".configurationModal");
  let parent = document.querySelector(".openMenu");

  if (container.classList.contains("active")) {
    container.classList.remove("active");
    parent.classList.remove("pressed");
  } else {
    container.style.display = "block";
    setTimeout(() => {
      container.classList.add("active");
      parent.classList.add("pressed");
    }, 10);
  }
}
window.toggleMenu = toggleMenu;

function openModalOnLoad() {
  let container = document.querySelector(".configurationModal");
  let parent = document.getElementById("toggleSidebar");

  container.style.display = "block";
  setTimeout(() => {
    container.classList.add("active");
    parent.classList.add("pressed");
  }, 10);
}

window.onload = openModalOnLoad;

window.addEventListener("load", () => {
  const listOptions = document.querySelectorAll(".blank");
  for (let i = 0; i < listOptions.length; i++) {
    const element = listOptions[i];
    element.addEventListener("click", () => {
      toggleList(element);
    });
  }
});

function toggleList(el) {
  let sumList = document.querySelectorAll(".blank");
  if (el.classList.contains("pressed")) {
    el.classList.remove("pressed");
    el.parentNode.querySelector("div").style.display = "none";
  } else {
    for (let i = 0; i < sumList.length; i++) {
      const element = sumList[i];
      element.classList.remove("pressed");
      element.parentNode.querySelector("div").style.display = "none";
    }
    el.classList.add("pressed");
    el.parentNode.querySelector("div").style.display = "block";
  }
}

/**
 * Toggles the display of a modal window.
 * @param {string} id - The ID of the modal to toggle.
 */
function toggleModal(id) {
  if (id == "save-modal" || id == "new-modal" || id == "rename-modal") {
    if (id == "save-modal" && user_authenticated == false) {
      // saveDesign();
      return;
    }
    document.getElementById(id).style.display = "flex";
    document.getElementById("backgroundModal").style.display = "flex";
  } else {
    if (id == "options-saved-design") {
      // NOTE: Replaced with ViewDesignsModal - old dropdown no longer used
      // if (document.getElementById(id).style.display == "none") {
      //   document.getElementById("options-saved-design").style.display = "block";
      //   document.getElementById("options-example").style.display = "none";
      //   // Refresh saved designs list when opening
      //   log("Refreshing saved designs list...");
      //   getSavedDesigns(user_data, showSavedDesigns);
      // } else document.getElementById(id).style.display = "none";
    } else if (id == "options-example") {
      if (document.getElementById(id).style.display == "none") {
        document.getElementById("options-example").style.display = "flex";
        document.getElementById("options-saved-design").style.display = "none";
      } else document.getElementById(id).style.display = "none";
    } else {
      if (document.getElementById(id).style.display == "none")
        document.getElementById(id).style.display = "block";
      else document.getElementById(id).style.display = "none";
    }
  }
}
window.toggleModal = toggleModal;

document.addEventListener("click", function (event) {
  let dropdownMenu1 = document.getElementById("save-menu-dropdown");
  if (
    dropdownMenu1.style.display === "flex" &&
    !dropdownMenu1.contains(event.target)
  ) {
    dropdownMenu1.style.display = "none";
  }
  let dropdownMenu2 = document.getElementById("cart-dropdown");
  if (
    dropdownMenu2.style.display === "flex" &&
    !dropdownMenu2.contains(event.target)
  ) {
    dropdownMenu2.style.display = "none";
  }
  let dropdownMenu3 = document.getElementById("actions-dropdown-button");
  if (
    dropdownMenu3.style.display === "flex" &&
    !dropdownMenu3.contains(event.target)
  ) {
    dropdownMenu3.style.display = "none";
  }
});

// Duplicate sign-out handler removed - handled above with LoginManager integration

document.addEventListener("mousedown", (event) => {
  var target = event.target;
  var blanks = Array.from(document.querySelectorAll(".blank"));
  var isClickedOutside = !blanks.some(function (blank) {
    return blank.contains(target) || target.closest(".options-modal");
  });

  if (isClickedOutside) {
    blanks.forEach(function (blank) {
      blank.classList.remove("pressed");
      blank.parentNode.querySelector("div.options-modal").style.display =
        "none";
    });
    var savedDesigns = document.getElementById("savedDesigns");
    if (savedDesigns && !savedDesigns.contains(target)) {
      savedDesigns.style.display = "none";
    }
    var options_design = document.getElementById("options-saved-design");
    if (options_design && !options_design.contains(target)) {
      options_design.style.display = "none";
    }
    var options_example = document.getElementById("options-example");
    if (options_example && !options_example.contains(target)) {
      options_example.style.display = "none";
    }
  }
});

let dashboard = document.getElementById("dashboard");
let trigger = document.getElementById("dashboardTrigger");
let viewer = document.querySelector(".viewer-wrapper");
trigger.addEventListener("click", () => {
  if (dashboard.style.display == "block") {
    dashboard.style.display = "none";
    trigger.style.backgroundColor = "";
    viewer.style.display = "block";
  } else {
    dashboard.style.display = "block";
    trigger.style.backgroundColor = "#fff";
    viewer.style.display = "none";
  }
});

function handleFileUpload(files) {
  log("Files:", files);
}

document.ondragover = document.ondrop = function (e) {
  e.preventDefault();
};

document.getElementById("drop-zone").ondragover = function () {
  this.classList.add("highlight");
};

document.getElementById("drop-zone").ondragleave = function () {
  this.classList.remove("highlight");
};

document.getElementById("drop-zone").ondrop = function (e) {
  e.preventDefault();
  this.classList.remove("highlight");
  var files = e.dataTransfer.files;
  handleFileUpload(files);
};

/**
 * Updates the enabled state of a button based on the selected option.
 * @param {string} name - The name of the radio button group.
 */
function updateButtonState(name) {
  const selectedOption = document.querySelector(
    `input[name="${name}"]:checked`
  );
  const button = document.getElementById("load-example");
  button.disabled = !selectedOption;
}

window.updateButtonState = updateButtonState;

/**
 * Updates the enabled state of the footer button based on the selected option.
 */
function updateFooterButtonState() {
  log("footer started");
  const selectedOption = document.querySelector(
    'input[name="footer-example"]:checked'
  );
  log("selected option", selectedOption);
  const button = document.getElementById("footer-example");
  log("this is button", button);
  button.disabled = !selectedOption; // Enable button if an option is selected
}
window.updateFooterButtonState = updateFooterButtonState;

document.getElementById("load-example").addEventListener("click", () => {
  log("option-example button clicked");
  const selectedOption = document.querySelector(
    'input[name="example"]:checked'
  );
  log("selectedOption", selectedOption?.value);
  if (selectedOption) {
    eventHub.emit(`importExample:${selectedOption.value}`);
    log(`importExample:${selectedOption.value}`);
  }

  document.getElementById("loadModal").style.display = "none";
  document.getElementById("backgroundModal").style.display = "none";
});

document.getElementById("footer-example").addEventListener("click", () => {
  log("footer example clicked");
  const selectedOption = document.querySelector(
    'input[name="footer-example"]:checked'
  );
  log("selectedoption", selectedOption.value);
  if (selectedOption) {
    eventHub.emit(`importExample:${selectedOption.value}`);
    log(`importExample:${selectedOption.value}`);
  }
});

/**
 * Handles an action selected from a context menu.
 * @param {string} action - The selected action.
 */
function handleAction(action) {
  alert(`Selected Action: ${action}`);
  hideContextMenu();
}

/**
 * Authenticates the user and updates the UI.
 * @param {Object} data - User authentication data.
 * @param {Object} [project] - The project data, if any.
 */
function authenticatedUser(data, project = undefined) {
  user_authenticated = true;
  window.user_authenticated = true;
  user_data = data;
  if (project) {
    closeModal("backgroundModal");
    document.getElementById("toolbar-project-name-container").style.display =
      "flex";
    document.getElementById("toolbar-project-name").value = project.name;
  } else {
    // NOTE: Replaced with ViewDesignsModal - designs are now fetched on-demand when modal opens
    // No need to fetch here on initial auth
    // getSavedDesigns(data, showInitSavedDesigns);
  }

  document.getElementById("user-initial-view").style.display = "flex";
  document.getElementById("guest-initial-view").style.display = "none";
  document.getElementById("open_saved_design").style.display = "flex";
  document.querySelector("#sign-in").style.display = "none";
  document.querySelector("#signed").style.display = "flex";
  
  // Check for Admin-FE role - handle both postMessage format and eventHub format
  const roles = data?.roles || data?.data?.roles || [];
  const roleArray = Array.isArray(roles) 
    ? roles 
    : (typeof roles === 'object' && roles.length ? roles : []);
  
  if (roleArray.some((role) => {
    const roleName = typeof role === 'string' ? role : role?.name;
    return roleName === "Admin-FE";
  })) {
    document.getElementById("dashboardTrigger").style.display = "flex";
  } else {
    document.getElementById("dashboardTrigger").style.display = "none";
  }
}

/**
 * Handles a guest user session.
 * @param {Object} [project] - The project data, if any.
 */
function guest(project = undefined) {
  user_authenticated = false;
  window.user_authenticated = false;
  if (project) {
    closeModal("backgroundModal");
    document.getElementById("toolbar-project-name-container").style.display =
      "flex";
    document.getElementById("toolbar-project-name").value = project.name;
  }
  document.getElementById("user-initial-view").style.display = "none";
  document.getElementById("guest-initial-view").style.display = "flex";
  document.getElementById("open_saved_design").style.display = "none";
  document.querySelector("#signed").style.display = "none";
  document.querySelector("#sign-in").style.display = "flex";
}

// Listen to eventHub events from LoginManager (for Keycloak authentication)
// Note: postMessage listeners removed - all authentication now uses eventHub
eventHub.on("auth:login", (authData) => {
  log("Received auth:login event from LoginManager");
  // Format data to match what authenticatedUser expects
  // Note: getSavedDesigns now uses Keycloak tokens via eventHub, not from userData
  const formattedData = {
    roles: authData.roles || [],
    token: authData.token,
    data: {
      roles: authData.roles || [],
      token: authData.token
    }
  };
  authenticatedUser(formattedData);
});

eventHub.on("auth:guest", () => {
  log("Received auth:guest event from LoginManager");
  guest();
});

// Ensure toolbar auth UI also reacts to session expiration and explicit sign_out,
// so buttons and modals are hidden as soon as the session is no longer valid.
eventHub.on("sessionExpired", () => {
  log("Toolbar: sessionExpired received, switching to guest UI");
  guest();
});

eventHub.on("sign_out", () => {
  log("Toolbar: sign_out received, switching to guest UI");
  guest();
});
